import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { adminApi, CreateUserRequest } from '../services/adminApi';
import { Customer, Assistant, Workflow } from '../types';
import { UserIcon, CogIcon, LinkIcon, XMarkIcon, PlusIcon, KeyIcon } from '@heroicons/react/24/outline';
import CrmCredentialsModal from '../components/CrmCredentialsModal';

const AdminPanel: React.FC = () => {
  const [users, setUsers] = useState<Customer[]>([]);
  const [selectedUser, setSelectedUser] = useState<Customer | null>(null);
  const [userAssistants, setUserAssistants] = useState<Assistant[]>([]);
  const [userWorkflows, setUserWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkingAssistant, setLinkingAssistant] = useState('');
  const [linkingWorkflow, setLinkingWorkflow] = useState('');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [crmModalOpen, setCrmModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<{
    id: string;
    type: 'assistant' | 'workflow';
    name: string;
  } | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersData = await adminApi.getAllUsers();
      setUsers(usersData);
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserAgents = async (user: Customer) => {
    try {
      const [assistants, workflows] = await Promise.all([
        adminApi.getUserAssistants(user.id),
        adminApi.getUserWorkflows(user.id)
      ]);
      setUserAssistants(assistants);
      setUserWorkflows(workflows);
      setSelectedUser(user);
    } catch (error) {
      toast.error('Failed to load user agents');
      console.error('Error loading user agents:', error);
    }
  };

  const handleLinkAssistant = async () => {
    if (!selectedUser || !linkingAssistant.trim()) return;

    try {
      await adminApi.linkAssistantToUser(selectedUser.id, linkingAssistant.trim());
      toast.success('Assistant linked successfully');
      setLinkingAssistant('');
      loadUserAgents(selectedUser);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to link assistant');
    }
  };

  const handleLinkWorkflow = async () => {
    if (!selectedUser || !linkingWorkflow.trim()) return;

    try {
      await adminApi.linkWorkflowToUser(selectedUser.id, linkingWorkflow.trim());
      toast.success('Workflow linked successfully');
      setLinkingWorkflow('');
      loadUserAgents(selectedUser);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to link workflow');
    }
  };

  const handleUnlinkAssistant = async (assistantId: string) => {
    if (!selectedUser) return;

    try {
      await adminApi.unlinkAssistantFromUser(selectedUser.id, assistantId);
      toast.success('Assistant unlinked successfully');
      loadUserAgents(selectedUser);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unlink assistant');
    }
  };

  const handleUnlinkWorkflow = async (workflowId: string) => {
    if (!selectedUser) return;

    try {
      await adminApi.unlinkWorkflowFromUser(selectedUser.id, workflowId);
      toast.success('Workflow unlinked successfully');
      loadUserAgents(selectedUser);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unlink workflow');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await adminApi.createUser(newUser);
      toast.success('User created successfully');
      setShowCreateUserModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleOpenCrmModal = (agentId: string, agentType: 'assistant' | 'workflow', agentName: string) => {
    setSelectedAgent({ id: agentId, type: agentType, name: agentName });
    setCrmModalOpen(true);
  };

  const handleCloseCrmModal = () => {
    setCrmModalOpen(false);
    setSelectedAgent(null);
  };

  const handleCrmCredentialsUpdated = () => {
    if (selectedUser) {
      loadUserAgents(selectedUser);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-600 mt-2">Manage user agent assignments</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Users List */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <UserIcon className="h-6 w-6 mr-2" />
                  Users
                </h2>
                <button
                  onClick={() => setShowCreateUserModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Create User
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${selectedUser?.id === user.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                    onClick={() => loadUserAgents(user)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{user.name}</h3>
                        <p className="text-sm text-gray-600">{user.email}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {user.role}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* User Agents Management */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <CogIcon className="h-6 w-6 mr-2" />
                Agent Management
                {selectedUser && (
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    for {selectedUser.name}
                  </span>
                )}
              </h2>
            </div>

            {selectedUser ? (
              <div className="p-6 space-y-6">
                {/* Link New Assistant */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Link New Assistant</h3>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Assistant ID"
                      value={linkingAssistant}
                      onChange={(e) => setLinkingAssistant(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleLinkAssistant}
                      disabled={!linkingAssistant.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Link
                    </button>
                  </div>
                </div>

                {/* Link New Workflow */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Link New Workflow</h3>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Workflow ID"
                      value={linkingWorkflow}
                      onChange={(e) => setLinkingWorkflow(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleLinkWorkflow}
                      disabled={!linkingWorkflow.trim()}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Link
                    </button>
                  </div>
                </div>

                {/* Current Assistants */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Linked Assistants ({userAssistants.length})
                  </h3>
                  <div className="space-y-2">
                    {userAssistants.map((assistant) => (
                      <div key={assistant.vapi_assistant_id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{assistant.name || 'Unnamed Assistant'}</p>
                          <p className="text-sm text-gray-600">ID: {assistant.vapi_assistant_id}</p>
                          {assistant.has_crm_credentials && (
                            <p className="text-xs text-green-600 mt-1">✓ CRM configured</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleOpenCrmModal(assistant.vapi_assistant_id, 'assistant', assistant.name || 'Unnamed Assistant')}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Manage CRM credentials"
                          >
                            <KeyIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleUnlinkAssistant(assistant.vapi_assistant_id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Unlink assistant"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {userAssistants.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No assistants linked</p>
                    )}
                  </div>
                </div>

                {/* Current Workflows */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Linked Workflows ({userWorkflows.length})
                  </h3>
                  <div className="space-y-2">
                    {userWorkflows.map((workflow) => (
                      <div key={workflow.vapi_workflow_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{workflow.name || 'Unnamed Workflow'}</p>
                          <p className="text-sm text-gray-600">ID: {workflow.vapi_workflow_id}</p>
                          {workflow.has_crm_credentials && (
                            <p className="text-xs text-green-600 mt-1">✓ CRM configured</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleOpenCrmModal(workflow.vapi_workflow_id, 'workflow', workflow.name || 'Unnamed Workflow')}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Manage CRM credentials"
                          >
                            <KeyIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleUnlinkWorkflow(workflow.vapi_workflow_id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Unlink workflow"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {userWorkflows.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No workflows linked</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6">
                <p className="text-gray-500 text-center">Select a user to manage their agents</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New User</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter user name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'user' | 'admin' })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setNewUser({ name: '', email: '', password: '', role: 'user' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CRM Credentials Modal */}
      {selectedAgent && (
        <CrmCredentialsModal
          isOpen={crmModalOpen}
          onClose={handleCloseCrmModal}
          agentId={selectedAgent.id}
          agentType={selectedAgent.type}
          agentName={selectedAgent.name}
          onCredentialsUpdated={handleCrmCredentialsUpdated}
        />
      )}
    </div>
  );
};

export default AdminPanel;